import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';

import { environment } from '@env/environment';
import { AuthQuery } from '@app/auth/auth.query';
import { StammdatenService, Stammdaten } from '@app/services/stammdaten.service';
import { SettingsService } from '@app/services/settings.service';
import { TransferMandatService, TransferMandat, BulkDefaults } from '@app/services/transfer.service';

/**
 * Bulk-Import (clone of the legacy C# `frm_BulkEdit` "Liste erfassen").
 *
 * A keyboard-wedge barcode scanner types the Aktenzeichen + Enter; a minimal
 * draft transfer is created with the configured defaults and stamped with the
 * logged-in officer. The captured rows can be edited inline and completed later
 * (before the VSTV export). Photos can be attached for future AI OCR/RAG.
 */
@Component({
  selector: 'app-bulk-import',
  templateUrl: './bulk-import.component.html',
  styleUrls: ['./bulk-import.component.scss'],
})
export class BulkImportComponent implements OnInit {
  // 0 = Anzeige, 1 = BOM
  bom = 0;

  scanValue = '';
  creating = false;

  // rows captured today (own, for Erfasser; all for Editor/Admin)
  rows: TransferMandat[] = [];

  // bulk defaults loaded from the app settings (Barcode tab clone)
  defaults: BulkDefaults = {
    strafe: 20,
    kzland: 'A',
    kzbezirk: '',
    zone: 0,
    fahrzeugart: 0,
    bearbeiter: '',
  };

  stammdaten: Stammdaten = {
    farben: [],
    hersteller: [],
    streets: [],
    bezirke: [],
    lander: [],
    tatbestande: [],
    usage: 0,
  };

  // which row's photo-upload panel is open (keyed by aktenzeichen)
  activeUploadAkt: string | null = null;

  imageStorage = environment.serverUrl + '/';

  @ViewChild('scanInput') scanInput: ElementRef<HTMLInputElement>;

  pondOptions = {
    class: 'my-filepond',
    credits: false,
    multiple: true,
    maxFiles: 5,
    allowFileTypeValidation: true,
    acceptedFileTypes: ['image/png', 'image/jpeg'],
    labelFileTypeNotAllowed: 'File of invalid type: only JPG, PNG',
    fileValidateTypeLabelExpectedTypesMap: { 'image/jpeg': '.jpg', 'image/png': '.png' },
    allowImageResize: true,
    imageResizeTargetWidth: 2400,
    imageResizeTargetHeight: 1600,
    imageResizeMode: 'contain',
    imageResizeUpscale: true,
    labelIdle: 'Bilder hier ablegen oder <span class="filepond--label-action">durchsuchen</span>',
    server: {
      process: (fieldName, file, metadata, load, error, progress, abort) => {
        const akt = this.activeUploadAkt || '';
        const formData = new FormData();
        formData.append(fieldName, file, file.name);
        formData.append('filepond', file.name);

        formData.append('uid', '' + akt);
        formData.append('fid', '' + akt);
        formData.append('role', '3');
        formData.append('imgtype', '' + fieldName);

        formData.append('aktenzeichen', '' + akt);
        formData.append('did', '' + this.authQuery.getValue().dienstnr);
        formData.append('didstvo', '' + this.authQuery.getValue().dienstnr_stvo);
        formData.append('didname', '' + this.authQuery.getValue().dienstname);

        formData.append('apikey', '' + environment.api_key);
        formData.append('phone_id', '' + this.authQuery.getValue().phone_id);
        formData.append('token', '' + this.authQuery.getValue().access_token);
        formData.append('akey', '' + environment.api_key);

        const request = new XMLHttpRequest();
        request.open('POST', environment.serverUrl + '/filepond/process');
        request.setRequestHeader('Access-Control-Allow-Origin', '*');
        request.setRequestHeader('Access-Control-Allow-Headers', '*');
        request.setRequestHeader('Authorization', `Bearer ${this.authQuery.access_token}`);

        request.upload.onprogress = (e) => {
          progress(e.lengthComputable, e.loaded, e.total);
        };

        request.onload = () => {
          if (request.status >= 200 && request.status < 300) {
            load(request.responseText);
          } else {
            error('upload failed');
          }
        };

        request.send(formData);

        return {
          abort: () => {
            request.abort();
            abort();
          },
        };
      },
      revert: 'filepond/revert',
      restore: 'filepond/restore?id=',
      fetch: 'filepond/fetch?data=',
      load: 'filepond/load',
    },
  };

  constructor(
    private router: Router,
    private _snackBar: MatSnackBar,
    private authQuery: AuthQuery,
    private stammdatenService: StammdatenService,
    private settingsService: SettingsService,
    private transferService: TransferMandatService
  ) {}

  ngOnInit(): void {
    this.loadDefaults();
    this.loadStammdaten();
    this.loadTodaysRows();
  }

  // ------------------------------------------------------------------ loaders

  private loadDefaults(): void {
    this.settingsService.getSettings().subscribe(
      (s: any) => {
        if (!s) {
          return;
        }
        this.defaults = {
          strafe: this.toNumber(s.BULK_STRAFE, 20),
          kzland: s.BULK_LAND || this.defaults.kzland,
          kzbezirk: s.BULK_BEZIRK || '',
          zone: this.toNumber(s.BULK_ZONE, 0),
          fahrzeugart: this.toNumber(s.BULK_FAHRZEUGART, 0),
          bearbeiter: s.BULK_BEARBEITER || '',
        };
      },
      () => {
        // non-fatal: keep built-in defaults
      }
    );
  }

  private loadStammdaten(): void {
    this.stammdatenService.getStammdaten().subscribe(
      (response) => {
        const d = response.data;
        d.lander = this.sortByBezeichnung(d.lander);
        d.streets = this.sortByBezeichnung(d.streets);
        this.stammdaten = d;
      },
      () => {
        // non-fatal
      }
    );
  }

  private loadTodaysRows(): void {
    const filters = JSON.stringify([['Status', 'Entwurf']]);
    this.transferService.getTransferMandate(1, 300, 'id', 'desc', '', filters).subscribe(
      (table) => {
        const today = this.todayString();
        this.rows = (table?.data || []).filter((r) => ('' + (r.createdate ?? '')).trim() === today);
      },
      () => {
        // non-fatal
      }
    );
  }

  // ------------------------------------------------------------------ actions

  selectType(bom: number): void {
    this.bom = bom;
    this.focusScan();
  }

  get typeLabel(): string {
    return this.bom === 1 ? 'BOM' : 'Anzeige';
  }

  /** Triggered by Enter in the scan field (barcode wedge sends Enter). */
  onScanEnter(): void {
    const akt = ('' + this.scanValue).trim();
    if (akt === '') {
      this.notify('Bitte ein Aktenzeichen scannen oder eingeben.');
      return;
    }
    if (this.creating) {
      return;
    }

    // duplicate in the current session list?
    if (this.rows.some((r) => '' + r.aktenzeichen === akt)) {
      this.notify(`Aktenzeichen ${akt} ist bereits in der Liste.`);
      this.scanValue = '';
      this.focusScan();
      return;
    }

    this.creating = true;
    this.transferService.checkAktenzeichenExists(akt).subscribe(
      (exists) => {
        if (exists) {
          this.creating = false;
          this.notify(`Das Aktenzeichen ${akt} wird bereits verwendet.`);
          this.scanValue = '';
          this.focusScan();
          return;
        }
        this.doCreate(akt);
      },
      () => {
        // if the existence check fails, still attempt to create (best effort)
        this.doCreate(akt);
      }
    );
  }

  private doCreate(akt: string): void {
    this.transferService.createBulkScanItem(akt, this.bom, this.defaults).subscribe(
      (res) => {
        this.creating = false;
        if (res?.result === 1) {
          this.prependRow(akt, res?.id ?? 0);
          this.notify(`${this.typeLabel} ${akt} erfasst.`);
        } else {
          this.notify('Eintrag konnte nicht angelegt werden.');
        }
        this.scanValue = '';
        this.focusScan();
      },
      () => {
        this.creating = false;
        this.notify('Eintrag konnte nicht angelegt werden.');
        this.scanValue = '';
        this.focusScan();
      }
    );
  }

  private prependRow(akt: string, id: number): void {
    const now = new Date();
    const row: TransferMandat = {
      ...({} as TransferMandat),
      id: id,
      aktenzeichen: akt,
      bom: this.bom,
      kzland: this.defaults.kzland,
      kzbezirk: this.defaults.kzbezirk,
      kznummer: '',
      street: '',
      streetnbr: '',
      hersteller: '',
      farbe: '',
      strafe: this.defaults.strafe,
      fahrzeugart: this.defaults.fahrzeugart,
      zone: this.defaults.zone,
      person: this.defaults.bearbeiter || this.authQuery.getValue().dienstname,
      createdate: this.todayString(),
      createtime: this.timeString(now),
      status_id: '0',
      transferred: 0,
      notes: '',
      info2: '',
      info3: '',
      tatbestnr: '',
      tatzeit: '',
      tat_datum: this.todayString(),
      tat_zeit: this.timeString(now),
    };
    this.rows = [row, ...this.rows];
  }

  /** Persist an inline-edited row via the existing transfer update endpoint. */
  saveRow(row: TransferMandat): void {
    if (!row.id) {
      this.notify('Zeile noch nicht gespeichert - bitte neu laden.');
      return;
    }
    const payload: any = {
      id: row.id,
      aktenzeichen: row.aktenzeichen,
      plate: '',
      land: row.kzland || '',
      bezirk: row.kzbezirk || '',
      nummer: row.kznummer || '',
      strasse: row.street || '',
      hausnummer: row.streetnbr || '',
      hersteller: row.hersteller || '',
      farbe: row.farbe || '',
      fahrzeugart: row.fahrzeugart || 0,
      tatbestandNr: row.tatbestnr || '',
      konkretisierung: '',
      strafe: row.strafe || 0,
      anmerkungen: row.notes || '',
      internal_notes: row.info3 || '',
      probekennzeichen: row.info2 || '',
      tatzeit: row.tatzeit || '',
      tat_zeit: row.tat_zeit || '',
      tat_datum: row.tat_datum || '',
      tatzeit_bis_datum: row.tatzeit_bis_datum || '',
      tatzeit_bis_uhrzeit: row.tatzeit_bis_uhrzeit || '',
      bom: row.bom ?? 0,
    };

    this.transferService.updateTransfer(payload).subscribe(
      () => this.notify(`${row.aktenzeichen} gespeichert.`),
      () => this.notify(`${row.aktenzeichen} konnte nicht gespeichert werden.`)
    );
  }

  toggleUpload(row: TransferMandat): void {
    this.activeUploadAkt = this.activeUploadAkt === row.aktenzeichen ? null : row.aktenzeichen;
  }

  openInEditor(row: TransferMandat): void {
    if (!row.id) {
      return;
    }
    this.router.navigate([
      '/transfer/edit',
      { id: row.id, _uid: row.id, _fid: row.id, _role: 3, _country: row.plate || '', ia: 1 },
    ]);
  }

  // ------------------------------------------------------------------ helpers

  private focusScan(): void {
    setTimeout(() => this.scanInput?.nativeElement?.focus(), 0);
  }

  private sortByBezeichnung<T extends { bezeichnung: string }>(arr: T[]): T[] {
    if (!Array.isArray(arr)) {
      return [];
    }
    return [...arr].sort((a, b) =>
      ('' + (a?.bezeichnung ?? '')).localeCompare('' + (b?.bezeichnung ?? ''), 'de')
    );
  }

  private toNumber(value: any, fallback: number): number {
    const n = Number(value);
    return isNaN(n) ? fallback : n;
  }

  private todayString(): string {
    const d = new Date();
    const pad = (n: number) => ('' + n).padStart(2, '0');
    return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
  }

  private timeString(d: Date): string {
    const pad = (n: number) => ('' + n).padStart(2, '0');
    return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }

  private notify(msg: string): void {
    this._snackBar.open(msg, '', { duration: 3000, horizontalPosition: 'center', verticalPosition: 'top' });
  }
}
